import { createElement, useCallback, useMemo, useState } from 'react';
import { useDispatch } from 'react-redux';
import { ThemeProvider } from '@mui/material/styles';

import Grid from '@mui/material/Grid2';
import Container from '@mui/material/Container';
import Typography from '@mui/material/Typography';

import { theme as warecloudTheme } from '../../ui/theme';
import { useTranslation } from 'react-i18next';
import { useGetClouderStatus, useGetEnabledRoutes } from '../services';
import { EnabledRouteError, EnabledRouteLoader } from './components';
import { useGetClouderServices } from '../../common/services';
import { Tabs as WTabs } from '../../common/components';
import { useGetTabItems, useIdentityValidationFlow } from './hooks';
import { Chip } from '@mui/material';
import NewClouderModal from './components/NewClouderModal';
import {
  IS_CL_CONTEXT,
  IS_BR_CONTEXT,
  NOTIFICATION_TYPES,
} from '../../../config/consts/commons';
import { mapUserStatusToIdentityValidation } from './utils/identityValidation';
import { IdentityValidationFlow } from './ui/components/identityValidation';
import { setMessage } from '../../toasts/toastSlice';

function EnabledRoutesContainer() {
  const { t } = useTranslation(['glossary', 'description', 'system']);
  const dispatch = useDispatch();
  const [approvedIdentityRouteId, setApprovedIdentityRouteId] = useState(null);

  const {
    data: clouderServices,
    isLoading: isLoadingClouderServices,
    isError: isErrorClouderServices,
  } = useGetClouderServices();
  const tabItems = useGetTabItems();

  const tabItemsFiltered = useMemo(() => {
    return tabItems
      .filter((tab) =>
        clouderServices?.data?.some(
          (service) => service.id_service === tab.serviceId
        )
      )
      .map((tab, index) => ({ ...tab, value: index }));
  }, [clouderServices, tabItems]);

  const {
    data: enabledRoutesSummary,
    isLoading: loadingEnabledRoutes,
    isError: isErrorEnabledRoutes,
  } = useGetEnabledRoutes({
    routeType: tabItemsFiltered[0]?.routeType,
  });
  const { data: clouderStatus } = useGetClouderStatus({
    enabled: IS_BR_CONTEXT,
  });
  const isNewClouderData = enabledRoutesSummary?.data?.is_new_clouder;
  const isNewClouderFeatureEnabled =
    import.meta.env.VITE_NEW_CLOUDERS_FEATURE_ENABLED === 'true';
  const isNewClouder =
    isNewClouderData && IS_CL_CONTEXT && isNewClouderFeatureEnabled;

  const identityValidation = mapUserStatusToIdentityValidation(
    IS_BR_CONTEXT ? clouderStatus : null
  );
  const showBiometricValidation =
    IS_BR_CONTEXT && identityValidation.isRouteAssignmentBlocked;
  const handleIdentityValidationError = useCallback(() => {
    dispatch(
      setMessage({
        body: t('identity_validation_unavailable', { ns: 'system' }),
        type: NOTIFICATION_TYPES.ERROR,
      })
    );
  }, [dispatch, t]);
  const identityValidationFlow = useIdentityValidationFlow({
    state: identityValidation,
    onError: handleIdentityValidationError,
  });
  const clearApprovedIdentityRoute = useCallback(() => {
    setApprovedIdentityRouteId(null);
  }, []);
  const handleApprovedIdentityRoute = useCallback((routeId) => {
    if (!routeId) return;
    setApprovedIdentityRouteId(routeId);
  }, []);

  const tabItemsWithCounters = useMemo(() => {
    const totalRoutesByService =
      enabledRoutesSummary?.data?.total_routes_by_service || {};

    return tabItemsFiltered.map((tab) => ({
      ...tab,
      count: totalRoutesByService[tab.routeType] ?? 0,
      isIdentityValidationBlocked: showBiometricValidation,
      onRequireIdentityValidation: identityValidationFlow.openValidationFlow,
      approvedIdentityRouteId,
      onApprovedIdentityRouteConsumed: clearApprovedIdentityRoute,
    }));
  }, [
    enabledRoutesSummary,
    tabItemsFiltered,
    showBiometricValidation,
    identityValidationFlow.openValidationFlow,
    approvedIdentityRouteId,
    clearApprovedIdentityRoute,
  ]);

  const hasMultipleClouderServices = useMemo(() => {
    return clouderServices?.data?.length > 1;
  }, [clouderServices]);

  if (
    isLoadingClouderServices ||
    (tabItemsFiltered.length > 0 && loadingEnabledRoutes)
  ) {
    return (
      <Container maxWidth="md" sx={{ mt: 5 }}>
        <EnabledRouteLoader />
      </Container>
    );
  }

  if (isErrorEnabledRoutes || isErrorClouderServices) {
    return (
      <Container maxWidth="md" sx={{ mt: 5 }}>
        <EnabledRouteError />
      </Container>
    );
  }

  if (tabItemsFiltered.length === 0) {
    return (
      <Container maxWidth="md" sx={{ mt: 5 }}>
        <EnabledRouteError />
      </Container>
    );
  }

  return (
    <ThemeProvider theme={warecloudTheme}>
      <Container maxWidth="md">
        <Grid container sx={{ mt: 2 }}>
          {IS_BR_CONTEXT &&
            identityValidation.enabled &&
            (identityValidationFlow.isBlocked ||
              identityValidationFlow.dialog) && (
              <Grid size={12}>
                <IdentityValidationFlow
                  state={identityValidation}
                  flow={identityValidationFlow}
                  onApprovedRoute={handleApprovedIdentityRoute}
                />
              </Grid>
            )}
          <Grid container alignItems="center" sx={{ mb: 2 }}>
            <Grid container size={12} spacing={1} alignItems="center">
              <Grid size="auto">
                <Typography
                  component="h1"
                  variant="body1"
                  sx={{ fontWeight: 600, fontSize: 24 }}
                >
                  {t('routes', { ns: 'glossary' })}
                </Typography>
              </Grid>
              {!hasMultipleClouderServices && (
                <Grid size="auto">
                  <Chip
                    label={tabItemsWithCounters[0]?.count ?? 0}
                    color="primary"
                    size="small"
                  />
                </Grid>
              )}
            </Grid>

            <Grid size={12}>
              <Typography variant="body1" color="text.main">
                {t('routes.description', { ns: 'description' })}
              </Typography>
            </Grid>
          </Grid>

          <Grid size={12}>
            {hasMultipleClouderServices ? (
              <WTabs tabsItems={tabItemsWithCounters} />
            ) : (
              createElement(tabItemsFiltered[0]?.component, {
                routeType: tabItemsFiltered[0]?.routeType,
                isIdentityValidationBlocked: showBiometricValidation,
                onRequireIdentityValidation:
                  identityValidationFlow.openValidationFlow,
                approvedIdentityRouteId,
                onApprovedIdentityRouteConsumed: clearApprovedIdentityRoute,
              })
            )}
          </Grid>
        </Grid>
      </Container>
      {isNewClouder && <NewClouderModal />}
    </ThemeProvider>
  );
}

export default EnabledRoutesContainer;
